import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { useTabParam } from '@structural-climbing/ui'
import type { Discipline } from '@structural-climbing/validation'

import { useStandings } from './useStandings'
import type { StandingsPeriod, StandingsGender } from './useStandings'
import { ScoringCard } from './ScoringCard'
import { StandingsTable } from './StandingsTable'
import { MyRankBanner } from './MyRankBanner'
import { SurfaceCard } from '../../design/SurfaceCard'
import { EmptyState } from '../../design/EmptyState'
import { SkeletonCard, SkeletonRow } from '../../design/Skeleton'
import { FilterMenu } from '../../design/FilterMenu'
import { disciplinePath } from '../nav/navGroups'

// Both filters carry a key called "all", so they need distinct param names.
const PERIODS = ['all', 'month', 'week'] as const satisfies readonly StandingsPeriod[]
const GENDERS = ['all', 'male', 'female'] as const satisfies readonly StandingsGender[]

/** The board's filters, read from and written to the URL so a refresh keeps them. */
export function useStandingsFilters() {
  const [period, setPeriod] = useTabParam('period', PERIODS, 'all')
  const [gender, setGender] = useTabParam('gender', GENDERS, 'all')
  return { period, setPeriod, gender, setGender }
}

/**
 * Gender and period, as two dropdown chips beside the sheet's title. They were
 * two rows of chips that pushed the board down; the board already says which
 * slice it is showing through these two labels.
 */
export function StandingsFilters() {
  const { t } = useTranslation()
  const { period, setPeriod, gender, setGender } = useStandingsFilters()
  return (
    <div className="flex items-center gap-1.5">
      <FilterMenu
        aria-label={t('standings.genderFilterLabel')}
        value={gender}
        onChange={setGender}
        options={[
          { key: 'all', label: t('standings.filterAll') },
          { key: 'male', label: t('standings.filterMen') },
          { key: 'female', label: t('standings.filterWomen') },
        ]}
      />
      <FilterMenu
        aria-label={t('standings.periodFilterLabel')}
        value={period}
        onChange={setPeriod}
        align="end"
        options={[
          { key: 'all', label: t('standings.filterAllTime') },
          { key: 'month', label: t('standings.filterThisMonth') },
          { key: 'week', label: t('standings.filterThisWeek') },
        ]}
      />
    </div>
  )
}

interface Props {
  gymId: string
  gymSlug: string
  /** Lead and bouldering are scored on different scales, so each has its own board. */
  discipline: Discipline
}

/**
 * The gym's leaderboard, shown in the sheet that opens from the rank card on
 * the wall.
 *
 * Its filters (`StandingsFilters`) sit in the sheet's header, beside the title.
 */
export function StandingsBoard({ gymId, gymSlug, discipline }: Props) {
  const { getAccessTokenSilently, user } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { period, gender } = useStandingsFilters()

  const { data, loading, error } = useStandings(
    gymId,
    apiUrl,
    getAccessTokenSilently,
    period,
    gender,
    discipline,
  )

  const myUserId = user?.sub

  // Why you have no row of your own. Only ever shown once the fetch has
  // resolved: defaulting it while `data` is still null used to flash the
  // "log some routes" line at a member who has hundreds.
  const settled = Boolean(data) && !loading
  const showPrivateNote = settled && data!.myIsPrivate
  const showNoClimbsNote = settled && !data!.myIsPrivate && !data!.myEntry

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-3" data-testid="standings-loading">
        <SkeletonCard />
        <SkeletonCard lines={4} />
      </div>
    )
  }

  if (error) {
    return (
      <SurfaceCard className="p-5">
        <p className="text-sm text-danger">{t('common.errorDefault')}</p>
      </SurfaceCard>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Nothing here for a private account or a member with no ascents in
          this slice: both already get a line inside the TOP 10 card, and
          saying it twice on one screen is worse than saying it once. */}
      {data?.myEntry && <MyRankBanner myEntry={data.myEntry} standings={data.standings} />}

      <SurfaceCard className="py-3">
        <p className="eyebrow mb-3 px-4 text-foreground/40">{t('standings.topN')}</p>
        {loading ? (
          <div className="flex flex-col">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : (data?.standings.length ?? 0) === 0 ? (
          <EmptyState icon="🧗" title={t('standings.empty')} />
        ) : (
          <StandingsTable
            entries={data?.standings ?? []}
            myEntry={data?.myEntry ?? null}
            myUserId={myUserId}
            apiUrl={apiUrl}
          />
        )}

        {showPrivateNote && (
          <p className="mt-3 border-t border-divider px-4 pt-3 text-xs text-foreground/50">
            {t('standings.privateMessage')}
          </p>
        )}

        {showNoClimbsNote && (
          <p className="mt-3 border-t border-divider px-4 pt-3 text-xs text-foreground/50">
            {t('standings.noClimbsMessage')}{' '}
            <Link
              to={`/${gymSlug}/${disciplinePath(discipline)}/routes`}
              className="text-primary underline"
            >
              {t('standings.logRoutesLink')}
            </Link>
          </p>
        )}
      </SurfaceCard>

      <ScoringCard
        progressLink={`/${gymSlug}/progress?discipline=${discipline}`}
        discipline={discipline}
      />
    </div>
  )
}
